import { generateRandomId } from 'core/utils';
import { sanitizeText } from '.';
import { Months } from '../constants';
import {
  CitationData,
  CitationSourceEnum,
  Contributor,
  ContributorTypes,
  ICitedPages,
  IDate,
} from '../types';
import {
  CrossrefContributor,
  CrossrefPartialDate,
  CrossrefSearchApiResponse,
} from '../types/crossref';

const convertURL = (url: string) => {
  /**
   * Observed formats
   * - [http://somewebsite.com/link/to/journal](http://somewebsite.com/link/to/journal)
   * - http://somewebsite.com/link/to/journal
   */
  if (/^\[.*\)$/.test(url)) {
    return url.match(/\((https?:\/\/.*)\)$/i)?.[1];
  }

  return url;
};

const convertDate = (d?: CrossrefPartialDate['date-parts']): IDate | undefined => {
  if (!d || !d[0]) {
    return undefined;
  }

  return {
    year: d[0][0],
    month: Months[(d[0][1] ?? 0) - 1],
    day: d[0][2],
  };
};

const convertPublisherLocation = (p: string): { city?: string; state?: string } => {
  const [city, state, country] = p.split(/, ?/, 3).map((token) => token.trim());

  return {
    city: city || undefined,
    state: (() => {
      if (state || country) {
        return [state, country].filter(Boolean).join(', ');
      }

      return undefined;
    })(),
  };
};

const convertPages = (p: string): ICitedPages => {
  const [start, end] = p.split('-');

  return { start, end };
};

const convertContributor = (source: CrossrefContributor, type: ContributorTypes): Contributor => ({
  key: generateRandomId(),
  contributorType: type,
  lastName: source.family,
  firstName: source.given?.trim(),
  initials: (() => {
    const matches = source.given?.replace(/[^a-zA-Z- ]/g, '').match(/\b\w/g);
    if (matches) {
      return matches.join('. ').toUpperCase().concat('.');
    }
    return undefined;
  })(),
});

export const crossrefRespToCitation = (input: CrossrefSearchApiResponse) =>
  input.items
    ?.filter((searchItem) => Boolean(searchItem.title?.[0]?.trim?.()))
    ?.map((searchItem) => {
      const citation: CitationData = {
        publicationInfo: {},
        contributors: [],
        citationSource: CitationSourceEnum.JOURNAL,
        id: null,
        refId: '',
      };

      citation.publicationInfo = {
        title: sanitizeText(searchItem.title?.[0]?.trim?.() || searchItem.title?.[0]),
        doi: searchItem.DOI,
        url: convertURL(searchItem.URL),
        publisher: searchItem.publisher,
        publishDate: convertDate(searchItem.issued?.['date-parts']),
        containerTitle: sanitizeText(
          searchItem['container-title']?.[0]?.trim?.() || searchItem['container-title']?.[0]
        ),
        issue: searchItem.issue,
        volume: searchItem.volume,
        source: 'Crossref',
      };

      if (searchItem.issued?.['date-parts']?.[0]?.length) {
        citation.publicationInfo.status = 'published';
      }

      if (searchItem['publisher-location']) {
        const { city, state } = convertPublisherLocation(searchItem['publisher-location']);
        citation.publicationInfo.city = city;
        citation.publicationInfo.state = state;
      }

      if (searchItem.page) {
        citation.publicationInfo.citedPages = convertPages(searchItem.page);
      }

      if (searchItem.author?.length) {
        citation.contributors.push();
      }

      searchItem.author?.forEach((c) => {
        citation.contributors.push(convertContributor(c, ContributorTypes.AUTHORS));
      });

      searchItem.editor?.forEach((c) => {
        citation.contributors.push(convertContributor(c, ContributorTypes.EDITORS));
      });

      searchItem.translator?.forEach((c) => {
        citation.contributors.push(convertContributor(c, ContributorTypes.TRANSLATORS));
      });

      return citation;
    }) || [];
